import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AdminApi, InspectionSiteAdmin } from './admin-api';
import {
  createNativeAdminScheduleApi,
  createNativeBookingApi,
  type NativeAdminBlock,
  type NativeAdminSchedule,
} from './native-booking-api';
import {
  WEEKDAY_LABELS,
  minuteToTimeLabel,
  slotLocalTime,
  timeLabelToMinute,
  ymdInEkb,
} from './native-booking-presentation';
import { problemMessage } from './admin-presentation';

const HOUR_OPTIONS = Array.from({ length: 24 * 2 }, (_, i) => minuteToTimeLabel(i * 30));

type WeekdayRow = {
  weekday: number;
  open: boolean;
  start_minute: number | null;
  end_minute: number | null;
};

function defaultWeekly(): WeekdayRow[] {
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
    weekday,
    open: weekday <= 5,
    start_minute: 9 * 60,
    end_minute: 18 * 60,
  }));
}

function normalizeWeekly(schedule: NativeAdminSchedule | null): WeekdayRow[] {
  if (schedule === null || schedule.weekly.length === 0) {
    return defaultWeekly();
  }
  const byDay = new Map<number, WeekdayRow>();
  for (const row of schedule.weekly) {
    const start =
      row.start_minute ??
      (typeof row.startMinute === 'number' ? row.startMinute : 9 * 60);
    const end =
      row.end_minute ?? (typeof row.endMinute === 'number' ? row.endMinute : 18 * 60);
    byDay.set(row.weekday, {
      weekday: row.weekday,
      open: row.open,
      start_minute: start,
      end_minute: end,
    });
  }
  return [1, 2, 3, 4, 5, 6, 7].map(
    (weekday) =>
      byDay.get(weekday) ?? {
        weekday,
        open: false,
        start_minute: 9 * 60,
        end_minute: 18 * 60,
      },
  );
}

function scheduleSlotDuration(schedule: NativeAdminSchedule | null): 30 | 60 {
  const value = schedule?.slot_duration_minutes;
  return value === 30 ? 30 : 60;
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = Date.UTC(y!, m! - 1, d! + days);
  const dt = new Date(utc);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function NativeSiteSchedulesAdmin(props: {
  readonly apiBaseUrl: string;
  readonly adminApi: AdminApi;
  readonly accessToken: string;
}): React.ReactElement {
  const scheduleApi = useMemo(
    () => createNativeAdminScheduleApi(props.apiBaseUrl, props.accessToken),
    [props.accessToken, props.apiBaseUrl],
  );
  const publicApi = useMemo(() => createNativeBookingApi(props.apiBaseUrl), [props.apiBaseUrl]);
  const [sites, setSites] = useState<readonly InspectionSiteAdmin[]>([]);
  const [siteId, setSiteId] = useState('');
  const [schedule, setSchedule] = useState<NativeAdminSchedule | null>(null);
  const [slotDuration, setSlotDuration] = useState<30 | 60>(60);
  const [weekly, setWeekly] = useState<WeekdayRow[]>(defaultWeekly());
  const [breakEnabled, setBreakEnabled] = useState(false);
  const [breakStart, setBreakStart] = useState('13:00');
  const [breakEnd, setBreakEnd] = useState('14:00');
  const [exceptionDate, setExceptionDate] = useState(() => ymdInEkb(new Date()));
  const [exceptionClosed, setExceptionClosed] = useState(true);
  const [previewDate, setPreviewDate] = useState(() => ymdInEkb(new Date()));
  const [previewSlots, setPreviewSlots] = useState<
    readonly { start_at: string; available: boolean }[]
  >([]);
  const [blockSlots, setBlockSlots] = useState<
    readonly { start_at: string; available: boolean }[]
  >([]);
  const [blocks, setBlocks] = useState<readonly NativeAdminBlock[]>([]);
  const [blockDate, setBlockDate] = useState(() => ymdInEkb(new Date()));
  const [blockSlotIso, setBlockSlotIso] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const rows = await props.adminApi.listInspectionSites();
        const active = rows.filter((row) => row.status === 'ACTIVE');
        setSites(active);
        if (active.length > 0) {
          setSiteId(active[0]!.id);
        }
      } catch (failure) {
        setError(problemMessage(failure));
      }
    })();
  }, [props.adminApi]);

  const loadSchedule = useCallback(async () => {
    if (siteId === '') {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await scheduleApi.getSchedule(siteId);
      setSchedule(body);
      setSlotDuration(scheduleSlotDuration(body));
      setWeekly(normalizeWeekly(body));
      const firstBreak = body.breaks[0];
      if (firstBreak !== undefined) {
        setBreakEnabled(true);
        setBreakStart(minuteToTimeLabel(firstBreak.start_minute));
        setBreakEnd(minuteToTimeLabel(firstBreak.end_minute));
      } else {
        setBreakEnabled(false);
      }
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  }, [scheduleApi, siteId]);

  const loadBlocks = useCallback(async () => {
    if (siteId === '') {
      setBlocks([]);
      return;
    }
    try {
      const from = ymdInEkb(new Date());
      const to = addDaysYmd(from, 60);
      const rows = await scheduleApi.listBlocks(siteId, from, to);
      setBlocks(rows);
    } catch (failure) {
      setError(problemMessage(failure));
    }
  }, [scheduleApi, siteId]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    void loadBlocks();
  }, [loadBlocks]);

  const loadPreview = useCallback(async () => {
    if (siteId === '') {
      return;
    }
    try {
      const body = await publicApi.availability(siteId, previewDate);
      setPreviewSlots(body.slots);
    } catch (failure) {
      setError(problemMessage(failure));
    }
  }, [previewDate, publicApi, siteId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const loadBlockDaySlots = useCallback(async () => {
    if (siteId === '' || blockDate === '') {
      setBlockSlots([]);
      return;
    }
    try {
      const body = await publicApi.availability(siteId, blockDate);
      setBlockSlots(body.slots);
      setBlockSlotIso((current) => {
        const stillValid = body.slots.some((slot) => slot.start_at === current);
        if (stillValid) {
          return current;
        }
        const preferred = body.slots.find((slot) => slot.available) ?? body.slots[0];
        return preferred?.start_at ?? '';
      });
    } catch {
      setBlockSlots([]);
    }
  }, [blockDate, publicApi, siteId]);

  useEffect(() => {
    void loadBlockDaySlots();
  }, [loadBlockDaySlots]);

  const buildBreaks = () =>
    breakEnabled
      ? weekly
          .filter((d) => d.open)
          .map((d) => ({
            weekday: d.weekday,
            start_minute: timeLabelToMinute(breakStart),
            end_minute: timeLabelToMinute(breakEnd),
          }))
      : [];

  const buildPutBody = (exceptions: NativeAdminSchedule['exceptions']) => ({
    expected_version: schedule?.version ?? '0',
    slot_duration_minutes: slotDuration,
    weekly: weekly.map((row) => ({
      weekday: row.weekday,
      open: row.open,
      start_minute: row.open ? row.start_minute : null,
      end_minute: row.open ? row.end_minute : null,
    })),
    breaks: buildBreaks(),
    exceptions,
  });

  const saveSchedule = async () => {
    if (siteId === '') {
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const exceptions = [...(schedule?.exceptions ?? [])];
      const saved = await scheduleApi.putSchedule(siteId, buildPutBody(exceptions));
      setSchedule(saved);
      setMessage('Расписание сохранено.');
      await loadPreview();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const removeException = async (date: string) => {
    if (siteId === '' || schedule === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const nextExceptions = (schedule.exceptions ?? []).filter((row) => row.date !== date);
      const saved = await scheduleApi.putSchedule(siteId, buildPutBody(nextExceptions));
      setSchedule(saved);
      setMessage('Исключение удалено.');
      await loadPreview();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const addException = async () => {
    if (siteId === '' || schedule === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const nextExceptions = [
        ...(schedule.exceptions ?? []).filter((row) => row.date !== exceptionDate),
        {
          date: exceptionDate,
          is_closed: exceptionClosed,
          start_minute: exceptionClosed ? null : (weekly.find((d) => d.open)?.start_minute ?? 540),
          end_minute: exceptionClosed ? null : (weekly.find((d) => d.open)?.end_minute ?? 1080),
        },
      ];
      const saved = await scheduleApi.putSchedule(siteId, buildPutBody(nextExceptions));
      setSchedule(saved);
      setMessage(exceptionClosed ? 'Выходной день добавлен.' : 'Исключение сохранено.');
      await loadPreview();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const addBlock = async () => {
    if (blockSlotIso.trim() === '') {
      setError('Выберите слот для блокировки.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await scheduleApi.addBlock(siteId, blockSlotIso.trim(), null);
      setMessage('Слот заблокирован.');
      await loadBlocks();
      await loadPreview();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const removeBlock = async (blockId: string) => {
    setBusy(true);
    setError(null);
    try {
      await scheduleApi.deleteBlock(siteId, blockId);
      await loadBlocks();
      await loadPreview();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="native-admin-schedules">
      <h1>Расписание площадок</h1>
      <p className="meta">
        Настройте дни и часы приёма, перерывы и разовые блокировки. Длительность слота —{' '}
        {slotDuration} минут (ЕКБ).
      </p>
      {error !== null ? (
        <p role="alert" className="banner banner-closed">
          {error}
        </p>
      ) : null}
      {message !== null ? (
        <p role="status" className="banner banner-info">
          {message}
        </p>
      ) : null}

      <section className="native-admin-panel native-admin-schedules__panel">
        <h2>Площадка</h2>
        <div className="native-admin-schedules__site-row">
          <label className="native-admin-schedules__field" htmlFor="native_admin_site">
            <span className="native-admin-schedules__field-label">Площадка осмотра</span>
            <select
              id="native_admin_site"
              value={siteId}
              disabled={busy}
              onChange={(event) => {
                setSiteId(event.target.value);
                setBlockSlotIso('');
              }}
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.display_name}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="native-admin-slot-duration native-admin-schedules__duration">
            <legend>Длительность слота</legend>
            <div className="native-admin-schedules__duration-options">
              <label className="native-admin-schedules__check">
                <input
                  type="radio"
                  name="native_admin_slot_duration"
                  value="60"
                  checked={slotDuration === 60}
                  disabled={busy}
                  onChange={() => {
                    setSlotDuration(60);
                  }}
                />
                <span>60 минут</span>
              </label>
              <label className="native-admin-schedules__check">
                <input
                  type="radio"
                  name="native_admin_slot_duration"
                  value="30"
                  checked={slotDuration === 30}
                  disabled={busy}
                  onChange={() => {
                    setSlotDuration(30);
                  }}
                />
                <span>30 минут</span>
              </label>
            </div>
          </fieldset>
        </div>
      </section>

      <section className="native-admin-panel native-admin-schedules__panel">
        <h2>Регулярное расписание</h2>
        <div className="native-admin-weekdays" data-testid="native-admin-weekdays">
          {weekly.map((row) => (
            <button
              key={row.weekday}
              type="button"
              className={row.open ? 'selected' : undefined}
              disabled={busy}
              onClick={() => {
                setWeekly((prev) =>
                  prev.map((item) =>
                    item.weekday === row.weekday ? { ...item, open: !item.open } : item,
                  ),
                );
              }}
            >
              {WEEKDAY_LABELS[row.weekday - 1]}
            </button>
          ))}
        </div>
        <div className="native-admin-schedules__time-row" data-testid="native-admin-hours-row">
          <label className="native-admin-schedules__field">
            <span className="native-admin-schedules__field-label">Начало</span>
            <select
              value={minuteToTimeLabel(weekly.find((d) => d.open)?.start_minute ?? 540)}
              disabled={busy}
              onChange={(event) => {
                const minute = timeLabelToMinute(event.target.value);
                setWeekly((prev) =>
                  prev.map((row) => (row.open ? { ...row, start_minute: minute } : row)),
                );
              }}
            >
              {HOUR_OPTIONS.map((opt) => (
                <option key={`s-${opt}`} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
          <label className="native-admin-schedules__field">
            <span className="native-admin-schedules__field-label">Окончание</span>
            <select
              value={minuteToTimeLabel(weekly.find((d) => d.open)?.end_minute ?? 1080)}
              disabled={busy}
              onChange={(event) => {
                const minute = timeLabelToMinute(event.target.value);
                setWeekly((prev) =>
                  prev.map((row) => (row.open ? { ...row, end_minute: minute } : row)),
                );
              }}
            >
              {HOUR_OPTIONS.map((opt) => (
                <option key={`e-${opt}`} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="native-admin-schedules__check native-admin-schedules__break-toggle">
          <input
            type="checkbox"
            checked={breakEnabled}
            disabled={busy}
            onChange={(event) => {
              setBreakEnabled(event.target.checked);
            }}
          />
          <span>Перерыв в приёме (для всех рабочих дней)</span>
        </label>
        {breakEnabled ? (
          <div className="native-admin-schedules__time-row" data-testid="native-admin-break-row">
            <label className="native-admin-schedules__field">
              <span className="native-admin-schedules__field-label">Начало перерыва</span>
              <select
                value={breakStart}
                disabled={busy}
                onChange={(e) => setBreakStart(e.target.value)}
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={`bs-${opt}`} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
            <label className="native-admin-schedules__field">
              <span className="native-admin-schedules__field-label">Окончание перерыва</span>
              <select value={breakEnd} disabled={busy} onChange={(e) => setBreakEnd(e.target.value)}>
                {HOUR_OPTIONS.map((opt) => (
                  <option key={`be-${opt}`} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
        <div className="actions native-admin-schedules__actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => void saveSchedule()}
          >
            Сохранить расписание
          </button>
        </div>
      </section>

      <section className="native-admin-panel native-admin-schedules__panel">
        <h2>Исключения / выходные</h2>
        <div className="native-admin-schedules__compact-row" data-testid="native-admin-exception-row">
          <label className="native-admin-schedules__field">
            <span className="native-admin-schedules__field-label">Дата</span>
            <input
              type="date"
              value={exceptionDate}
              disabled={busy}
              onChange={(event) => {
                setExceptionDate(event.target.value);
              }}
            />
          </label>
          <label className="native-admin-schedules__check native-admin-schedules__compact-check">
            <input
              type="checkbox"
              checked={exceptionClosed}
              disabled={busy}
              onChange={(event) => {
                setExceptionClosed(event.target.checked);
              }}
            />
            <span>Закрытый день</span>
          </label>
          <div className="native-admin-schedules__compact-action">
            <button
              type="button"
              className="btn-primary"
              disabled={busy || schedule === null}
              onClick={() => void addException()}
            >
              Добавить исключение
            </button>
          </div>
        </div>
        <ul className="native-admin-blocks">
          {(schedule?.exceptions ?? []).map((row) => (
            <li key={row.date}>
              <span>
                {row.date} — {row.is_closed ? 'выходной' : 'особый режим'}
              </span>
              <button
                type="button"
                disabled={busy || schedule === null}
                onClick={() => {
                  void removeException(row.date);
                }}
              >
                Удалить
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="native-admin-panel native-admin-schedules__panel">
        <h2>Блокировки слотов</h2>
        <div className="native-admin-schedules__compact-row" data-testid="native-admin-block-row">
          <label className="native-admin-schedules__field">
            <span className="native-admin-schedules__field-label">Дата</span>
            <input
              type="date"
              value={blockDate}
              disabled={busy}
              onChange={(event) => {
                setBlockDate(event.target.value);
                setBlockSlotIso('');
              }}
            />
          </label>
          <label className="native-admin-schedules__field">
            <span className="native-admin-schedules__field-label">Слот (ЕКБ)</span>
            <select
              value={blockSlotIso}
              disabled={busy}
              onChange={(event) => {
                setBlockSlotIso(event.target.value);
              }}
            >
              <option value="">Выберите слот</option>
              {blockSlots.map((slot) => (
                <option key={slot.start_at} value={slot.start_at}>
                  {slotLocalTime(slot.start_at)} {slot.available ? '' : '(занят)'}
                </option>
              ))}
            </select>
          </label>
          <div className="native-admin-schedules__compact-action">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => void addBlock()}
            >
              Заблокировать слот
            </button>
          </div>
        </div>
        <ul className="native-admin-blocks">
          {blocks.length === 0 ? <li>Нет сохранённых блокировок в ближайшие 60 дней.</li> : null}
          {blocks.map((block) => (
            <li key={block.id}>
              <span>{formatEkb(block.slot_start_at)}</span>
              <button type="button" disabled={busy} onClick={() => void removeBlock(block.id)}>
                Снять
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="native-admin-panel native-admin-schedules__panel">
        <h2>Доступность (просмотр)</h2>
        <div className="native-admin-schedules__preview-head">
          <label className="native-admin-schedules__field native-admin-schedules__preview-date">
            <span className="native-admin-schedules__field-label">Дата</span>
            <input
              type="date"
              value={previewDate}
              disabled={busy}
              onChange={(event) => {
                setPreviewDate(event.target.value);
              }}
            />
          </label>
        </div>
        <ul className="native-admin-slot-preview">
          {previewSlots.map((slot) => (
            <li key={slot.start_at} className={slot.available ? 'free' : 'busy'}>
              {slotLocalTime(slot.start_at)} — {slot.available ? 'свободно' : 'недоступно'}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function formatEkb(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'Asia/Yekaterinburg',
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
